import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment-timezone';
import swal from 'sweetalert';
import { format as formatDate } from 'date-fns';
import { useImmer } from 'use-immer';
import { immerable } from 'immer';
import _ from 'lodash';
import { SHOWING_PROVIDERS_CONST } from '@bb/common';
import errorReporter from '@app/errorReporter';
import Swal from 'sweetalert2';
import axios from 'axios';
import { UserRoles } from '@bb/users';
import { useHistory } from 'react-router-dom';
import {
  isAssumeOrgType,
  isAuthForLocation,
  isAuthForRole,
  getStartOfDayInTimezone,
  isFullAccessUser,
  isManualCoopUserWithAccess,
  featureEnabled,
} from 'utils';
import { BuyerAgreementContext } from 'contexts';
import { Modal, message, Text, BuyerAgreementCheckbox } from 'components';
import { usePrevious, useListing } from 'hooks';
import { UserController } from '../../../../api';
import {
  UNVERIFIED_AGENT_MODAL,
  UNVERIFIED_ADMIN_MODAL,
} from '../../../constants/index';
import VirtualTourTab from './virtual_tour/VirtualTourTab';
import styles from './appointment_book.module.scss';
import AppointmentHeader from './AppointmentHeader';
import ListingPreview from '../../preview/listing_preview.component';
import PreBookingInfoAlert from './PreBookingInfoAlert';
import Step1User from './Step1User';
import Step23AppointmentBooking from './Step23AppointmentBooking';

import VtAppointmentBooking from './virtual_tour/VtAppointmentBooking';

import Step4Mobile from './Step4Mobile';
import MobileButtons from './MobileButtons';
import MainContainer from './MainContainer';
import {
  AppointmentOverride,
  getShowingScreenType,
  ShowingScreenType,
} from './AppointmentOverride';
import AppointmentSubHeader from './AppointmentSubHeader';
import InaccessibleListingPanelWrapper from './InaccessibleListingPanelWrapper';
import { HOME_INSPECTION_TYPES } from '../../constants';

const DEFAULT_APPOINTMENT_TYPES = [
  'Buyer/Broker',
  'Buyer Visit',
  'Agent Inspection',
  'Revisit',
  ...(featureEnabled('transactionBrokerTypeFeature')
    ? ['Transaction Broker']
    : []),
  'Dual Agency',
  'Home Inspection',
  'Appraisal',
  'Photographer',
  'Pest Control',
  ...HOME_INSPECTION_TYPES,
  'Information Request',
  'Final Walkthrough',
];

// Determines the appointment types to show for a listing based on the showing provider and appointment config
const getAppointmentTypesForListing = (listing, appointmentConfig) => {
  if (listing?.showingProvider === SHOWING_PROVIDERS_CONST.SHOWINGTIME) {
    // Check if appointment config has mapped allowed appointment types for ShowingTime
    const mappedTypes =
      appointmentConfig?.showingProviderAdditionalInfo
        ?.mappedAllowedAppointmentTypes;
    if (mappedTypes && Array.isArray(mappedTypes) && mappedTypes.length > 0) {
      return mappedTypes;
    }
    return ['Buyer/Broker'];
  }

  return DEFAULT_APPOINTMENT_TYPES;
};

// The appointment booking page was split into an internal and external component since they are very different booking flows
// Not all external logic has been extracted out of this component yet so you may see some duplicated or unused code
// If possible, try to remove dead code and/or DRY up duplicated code
const AppointmentBookingInternal = props => {
  // props passed in through the apointmentbooking.module.js
  // apointmentDetails were part of the bindings
  const {
    fetchSavedAppointment,
    appointmentDetails,
    isListingContact,
    isDoneWelcomeFlow,
    $location,
    $rootScope,
    $routeParams,
    $anchorScroll,
    ErrorService,
    AppointmentBookService,
    UtilService,
    ModalService,
  } = props;

  // hook to use Immer for easy immutable state change
  const [state, updateState] = useImmer({
    appointment: {},
    appointmentConfig: null,
    offerDetails: null,
    interval: 15,
    duration: 30,
    hours: [0, 1, 2, 3, 4, 5, 6],
    minutes: [0, 15, 30, 45],
    customDuration: {
      enabled: false,
      durationMinutes: 0,
      durationHour: 1,
    },
    enableOpenHouses: false,
    allAppointments: [],
    customContact: null,
    customContactSaved: false,
    nonAgent: {},
    customAgent: {},
    nextStepLabel: 'Next >',
    appointmentTypes: DEFAULT_APPOINTMENT_TYPES,
    nonAgentTypes: [
      'Appraisal',
      'Home Inspection',
      'Photographer',
      'Pest Control',
    ],
    naTypeMap: {
      Appraisal: 'Appraiser',
      ...HOME_INSPECTION_TYPES.reduce(
        (accum, type) => ({ ...accum, [type]: type }),
        {},
      ),
      'Home Inspection': 'Home Inspector',
      Photographer: 'Photographer',
      'Pest Control': 'Pest Control',
      'Buyer/Broker': 'Buyer/Broker',
      'Buyer Visit': 'Buyer',
      'Agent Inspection': 'Agent Inspector/Previewer',
      Revisit: 'Revisit',
      'Dual Agency': 'Dual Agency',
    },
    mobileWorkflowSteps: {
      initialStep: 0,
      selectDateStep: 1,
      selectTimeStep: 2,
      confirmDetailsStep: 3,
    },
    selectedTimes: {},
    invitees: [],
    keepOriginalAppointment: true,
    haveListingPermission: false,
    canBookAppointment: true,
    customContactPreApproved: false,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [partialAutoConfirm, setPartialAutoConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMobileNumber, setHasMobileNumber] = useState(true);
  const [prevDate, setPrevDate] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [showingOverrideType, setShowingOverrideType] = useState(null);
  const [isBookingAllowed, setIsBookingAllowed] = useState(true);
  const [unverifiedModalState, setUnverifiedModalState] = useState({
    shouldDisplay: false,
    redirect: '',
    isAgent: true,
  });
  const prevShowingOverrideType = usePrevious(showingOverrideType);
  const history = useHistory();

  const [inviteBuyers, setInviteBuyers] = useState([]);

  const { hasBuyerAgreed, showBuyerAgreementModal } = useContext(
    BuyerAgreementContext,
  );
  const { hasNarBuyerSettlement, loading: listingLoading } = useListing(
    state.listing?.id,
  );

  /**
   * @param {import('@bb/domain/dist/types').OrgFeatures} feature
   * @returns boolean
   */
  const hasFeature = feature =>
    state.listing?.org.features.includes(feature) ?? false;

  useEffect(() => {
    // checks if auth exist through org field
    if (!$rootScope.auth?.org) return;

    const isSameOrAnsweringOrg =
      ($rootScope.auth.org._id &&
        $rootScope.auth.org._id === state?.listing?.org._id) ||
      isAssumeOrgType($rootScope.auth.org.type);

    let canBookAppointment;
    if (!isSameOrAnsweringOrg) {
      const currentUserOrgMlsType = $rootScope.auth.org.extendedMlsTypes;
      const isSameMlsType = !!state?.listing?.org.mlsTypes.some(mlsType =>
        currentUserOrgMlsType.includes(mlsType),
      );

      // users from a different org and a different province can't book appointment
      canBookAppointment =
        isSameMlsType ||
        isManualCoopUserWithAccess($rootScope.auth, state.listing?.org);
    } else {
      canBookAppointment = true;
    }
    updateState(draft => ({ ...draft, canBookAppointment }));
  }, [$rootScope.auth, state?.listing, state?.haveListingPermission]);

  useEffect(
    () => () => {
      Swal.close();
      swal.close();
    },
    [history],
  );

  useEffect(() => {
    const isMobile = window.screen.width <= 767;
    if (isMobile) {
      setCurrentStep(state.mobileWorkflowSteps.initialStep);
    }
    updateState(draft => {
      draft.isMobile = isMobile;
      draft.mobileContentHeight = `${window.innerHeight - 101}px`;
    });

    // If existing appointment ie. timechange
    if (appointmentDetails) {
      const appointment = _.clone(appointmentDetails.appointment);
      appointment[immerable] = true;
      const appointmentTypesForListing = getAppointmentTypesForListing(
        appointmentDetails.listing,
        appointmentDetails.appointmentConfig,
      );
      updateState(draft => {
        draft.listing = appointmentDetails.listing;
        draft.listingId = appointmentDetails.listing.id;
        draft.appointmentTypes = appointmentTypesForListing;
        draft.appointment = appointment;
        if (!appointmentTypesForListing.includes(draft.appointment.type)) {
          const [firstAppointmentType] = appointmentTypesForListing;
          draft.appointment.type = firstAppointmentType;
        }
        draft.originalAppointment = appointmentDetails.appointment;
        draft.originalDate = draft.originalAppointment.time;
        draft.isTimeChange = true;
      });
      updateDate(appointmentDetails.appointment.date);
    } else {
      resetAppointment();
    }

    const listingId =
      state.listingId ||
      (state.appointment && state.appointment.listingId) ||
      $routeParams.listingId ||
      appointmentDetails.listing.id;
    const auth = _.cloneDeep($rootScope.auth);
    updateState(draft => {
      draft.auth = auth;
      draft.showNoteButton = false;
      draft.listingId = listingId;
    });

    AppointmentBookService.getAppointmentBookingInfo(listingId)
      .then(
        async ({
          listing: fetchedListing,
          unavailableDays: unavailable,
          externalConfig,
          offerDetails: details,
          phoneNumber,
          appointmentConfig: appointmentConfigData,
          blockedAppointments,
          serverDate,
        }) => {
          // eslint-disable-next-line no-underscore-dangle
          const _listing = appointmentDetails?.listing || fetchedListing;
          let listing;
          if (Object.prototype.hasOwnProperty.call(_listing, 'listing')) {
            listing = _listing.listing;
          } else {
            listing = _listing;
          }

          const teamIds = listing.reps
            .map(rep => rep.team)
            .filter(team => team);
          updateState(draft => {
            draft.phoneNumber =
              phoneNumber && phoneNumber.number
                ? phoneNumber.number
                : draft.auth.phone;
            draft.isTeamAdmin =
              draft.auth.role === UserRoles['Team Admin'] &&
              draft.auth.team &&
              teamIds.some(teamId => teamId === draft.auth.team);
            draft.isListingHiddenRep = listing.hiddenReps.some(
              ({ id }) => id === $rootScope.auth.id,
            );
          });

          updateState(draft => {
            const appointmentTypesForListing = getAppointmentTypesForListing(
              listing,
              appointmentConfigData,
            );
            draft.listing = listing;
            draft.appointmentTypes = appointmentTypesForListing;
            if (!listing.timezone) draft.listing.timezone = 'America/Toronto';
            if (
              draft.appointment &&
              !appointmentTypesForListing.includes(draft.appointment.type)
            ) {
              const [firstAppointmentType] = appointmentTypesForListing;
              draft.appointment.type = firstAppointmentType;
            }
            if (listing.org.settings) {
              draft.enableOpenHouses = listing.org.settings.enableOpenHouses;
            }

            draft.unavailableDays = unavailable.days;
            if (unavailable.after) {
              draft.unavailableAfter = new Date(unavailable.after);
            }
            draft.externalConfig = externalConfig;
            draft.isVirtualTourEnabled = externalConfig.isVirtualTourEnabled;
            draft.noMoreShowings =
              externalConfig.noMoreShowings &&
              (!!draft.auth || !isListingContact);
            draft.duration = externalConfig.maxDuration
              ? externalConfig.maxDuration
              : 60;
            draft.autoConfirm = externalConfig.autoConfirm;
            draft.advAutoConfEnabled = externalConfig.advAutoConfEnabled;
            draft.aptConfigDays = externalConfig.days;
            draft.aptConfigSpecialTimes = externalConfig.specialTimes;
            draft.maxDuration = draft.duration;
            draft.openHouseDays = draft.listing.openHouses.map(({ timeFrom }) =>
              formatDate(new Date(timeFrom), 'yyyy-MM-dd'),
            );
            draft.offerAnytime = true;
            draft.offerDetails = details;
            if (details?.offerSettings.acceptedAnytime !== undefined) {
              draft.offerAnytime = details.offerSettings.acceptedAnytime;
            }
            if (details?.offerSettings.offerDate) {
              const offerDate = moment(details.offerSettings.offerDate);
              offerDate.minute(
                Math.round(offerDate.minute() / draft.interval) *
                  draft.interval,
              );
              draft.offerDate = offerDate;
            }

            if (serverDate) {
              draft.serverDate = serverDate;
            }
          });
          // Check if can book for others
          let isAdmin;
          const isListingAgent =
            listing.reps.filter(
              rep => rep.email === ($rootScope.auth && $rootScope.auth.email),
            ).length > 0;
          const repsTeams = listing.reps.map(({ team }) => team);
          const isTeamAdmin = $rootScope.auth.role === UserRoles['Team Admin'];
          const isTeamAdminWithAccess =
            isTeamAdmin &&
            repsTeams.some(teamid => teamid === $rootScope.auth.team);

          if (
            $rootScope.auth.org?.id === listing.org.id &&
            ($rootScope.auth.roles[$rootScope.auth.role] >=
              $rootScope.auth.roles.GLOBAL_ADMIN ||
              ($rootScope.auth.role === 'BRANCH_ADMIN' &&
                isAuthForLocation($rootScope.auth, listing)) ||
              isTeamAdminWithAccess)
          ) {
            isAdmin = true;
          }

          const isListingUnclaimed =
            !isFullAccessUser($rootScope.auth) && listing.isUnclaimed;

          /**
           * Shows modal for unverified users, and redirect if they can book showing / offer
           */

          const denyAccess =
            !isFullAccessUser($rootScope.auth) && $rootScope.auth.isUnverified;

          if (denyAccess) {
            const isAgent = $rootScope.auth.role === UserRoles.Agent;
            const redirectAccess =
              (isAgent && !isListingAgent) || isListingUnclaimed;

            setUnverifiedModalState({
              shouldDisplay: true,
              isAgent,
              redirect: redirectAccess ? `#/listing/${listing.id}/view` : '',
            });

            if (redirectAccess) return;
          }

          /**
           * Also redirect if listing is unclaimed (no modal designed yet)
           */

          if (isListingUnclaimed) {
            $rootScope.$evalAsync(() => {
              $location.path(`/listing/${listing.id}/view`);
            });
            return;
          }

          // for answering service
          if (isAssumeOrgType($rootScope.auth.org.type)) {
            isAdmin = true;
          }
          updateState(draft => {
            draft.isAdmin = isAdmin;
            draft.isUnverified = $rootScope.auth.isUnverified;
          });
          listing.reps.forEach(rep => {
            if (rep.team)
              updateState(draft => {
                draft.listingTeam = rep.team;
              });
            // can book others if you are the listing agent
            if (rep.id === $rootScope.auth.id)
              updateState(draft => {
                draft.canBookOthers = true;
                draft.isListingRep = true;
              });
          });

          // can also book others if this is your team's listing
          updateState(draft => {
            draft.canBookOthers =
              (draft.listingTeam &&
                draft.listingTeam === $rootScope.auth.team) ||
              draft.canBookOthers;
            if (
              isAdmin ||
              (!state.haveListingPermission &&
                $rootScope.auth?.role &&
                $rootScope.auth.roles[$rootScope.auth.role] >=
                  $rootScope.auth.roles.TEAM_ADMIN)
            ) {
              draft.bookFor = 'others';
            } else {
              draft.bookFor = $rootScope.auth.isUnverified ? 'others' : 'self';
            }
            draft.isListingAgent = isListingAgent;
            // must be a team admin who is a licensed agent or an agent
            draft.canBookOthers =
              !isAdmin &&
              ((draft.auth && draft.auth.role !== UserRoles['Team Admin']) ||
                draft.auth?.isLicensed) &&
              draft.canBookOthers;
            // If user is not able to book others, move them to the date picker screen
            if (draft.isMobile && !draft.canBookOthers && !isAdmin) {
              setCurrentStep(state.mobileWorkflowSteps.selectDateStep);
            }
          });
          // setting Listing Permissions
          const isAssumed = isAssumeOrgType($rootScope.auth?.org?.type);
          const haveListingPermission =
            isAuthForRole({
              auth: $rootScope.auth,
              org: listing.org.id,
              locations: listing.locations,
              teams: [listing.team],
              reps: listing.reps,
            }) ||
            isAssumed ||
            listing.canEdit;

          if (haveListingPermission) {
            updateState(draft => {
              draft.haveListingPermission = true;
            });
          }

          // Service Requests //
          if (
            (isAdmin || isListingAgent || state.isTeamAdmin) &&
            haveListingPermission
          ) {
            updateState(draft => {
              if (appointmentConfigData) {
                draft.appointmentConfig = appointmentConfigData;
                draft.advAutoConfEnabled =
                  appointmentConfigData.advAutoConfEnabled;
                draft.aptConfigDays = appointmentConfigData.days;
                draft.aptConfigSpecialTimes =
                  appointmentConfigData.specialTimes;
                draft.autoConfirm = appointmentConfigData.autoConfirm;
              }
              if (blockedAppointments) {
                draft.appointments = blockedAppointments;
                draft.allAppointments = blockedAppointments || [];
              }
            });
          }

          if (
            state.isMobile &&
            ($rootScope.auth.role === UserRoles.Agent ||
              ($rootScope.auth.role === UserRoles['Team Admin'] &&
                $rootScope.auth.isLicensed &&
                !state.isTeamAdmin))
          )
            moveToStep(state.mobileWorkflowSteps.selectDateStep);

          setLoading(false);
        },
      )
      .catch(err => {
        // TOAST
        errorReporter.error(err);
      })
      .finally(() => {
        updateState(draft => {
          draft.dataLoaded = true;
          draft.showComingSoonWarning = false;
        });
      });
  }, []);

  // If Booking for self
  useEffect(() => {
    if (
      state.bookFor === 'self' &&
      !$rootScope.auth.isUnverified &&
      ($rootScope.auth.isLicensed ||
        ($rootScope.auth.supraSubscription.type === 'LIMITED' &&
          $rootScope.auth.features?.includes('SCHEDULING_SERVICES')))
    ) {
      populateInternalAppointment($rootScope.auth);
    }
  }, [state.bookFor]);

  useEffect(() => {
    if (state.savedAppointment) fetchSavedAppointment(state.savedAppointment);
  }, [state.savedAppointment]);

  useEffect(() => {
    if (state.dataLoaded && state.earliestAvailableTime && state.shouldScroll) {
      scrollToEarliestAvailableTime(state.earliestAvailableTime);
      updateState(draft => {
        draft.shouldScroll = false;
      });
    }
  }, [state.dataLoaded, state.earliestAvailableTime, state.shouldScroll]);

  useEffect(() => {
    if (
      state.advAutoConfEnabled &&
      (state.isAdmin || state.listing.canEdit || state.listing.canRead) &&
      state.selectedTimes &&
      Object.keys(state.selectedTimes).length
    )
      setPartialAutoConfirm(
        checkPartialAutoConfirm(
          state.aptConfigDays,
          state.aptConfigSpecialTimes,
          state.selectedTimes,
          state.selecteddate,
        ),
      );
  }, [state.selectedTimes]);

  useEffect(() => {
    if (!unverifiedModalState.shouldDisplay) return;
    // Only display after welcome modals are closed
    if (!isDoneWelcomeFlow) return;

    Modal.warning({
      ...(unverifiedModalState.isAgent
        ? UNVERIFIED_AGENT_MODAL
        : UNVERIFIED_ADMIN_MODAL),
      onOk:
        unverifiedModalState.redirect &&
        (() => {
          // In case we were redirected from some other logic
          if (!window.location.hash.includes(unverifiedModalState.redirect)) {
            window.location.href = unverifiedModalState.redirect;
          }
        }),
    });
  }, [unverifiedModalState, isDoneWelcomeFlow]);

  // *functions from appointment_book.component.js
  function appointmentInfo(dateStr) {
    return AppointmentBookService.getAppointmentInfo(
      state.listingId,
      'times',
      dateStr,
    );
  }

  // *functions
  function resetDate() {
    const comingSoonDate =
      state.listing.status === 'Coming Soon' &&
      getStartOfDayInTimezone(
        state.listing.coming_soon_date,
        state.listing.timezone,
      );
    updateState(draft => {
      const initialDate = comingSoonDate || new Date();
      draft.selecteddate = initialDate;
      draft.localdate = initialDate;
      draft.currentViewDate = initialDate;
      draft.appointment.recordAppointment = false;
    });
  }

  function populateInternalAppointment(user) {
    if (_.isEmpty(user)) return;
    const coopAddress = user.org.addresses[user.location][user.address];
    updateState(draft => {
      draft.appointment.coop = {
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.emails?.[0] || user.email || '',
        emails: user.emails,
        phone: draft.phoneNumber,
        text: user.text,
        orgFeatures: user.org.features,
      };
      draft.coopUserOrg = {
        name: user.org.name,
        address: coopAddress && coopAddress.address,
        phone: coopAddress && coopAddress.phone,
      };
      draft.sameOrg = user.org.id && user.org.id === draft.listing.org.id;
    });
  }

  /**
   * @param { 'text' | 'email' | 'email&text' | 'office' } option
   */
  const selectNotifyMethod = option =>
    updateState(draft => {
      draft.appointment.notify.email = false;
      draft.appointment.notify.text = false;
      draft.appointment.notify.office = false;
      if (option === 'email&text') {
        draft.appointment.notify.email = true;
        draft.appointment.notify.text = true;
      } else {
        draft.appointment.notify[option] = true;
      }
    });

  const isValidContact = () =>
    state.customContact &&
    state.customContact.firstName &&
    state.customContact.lastName &&
    (state.customContact.email || state.bookFor === 'nonAgent') &&
    state.customContact.org &&
    state.customContact.org.name &&
    state.customContact.org.phone &&
    state.appointment.type;

  const recordAppointment = () => {
    if (state.customContact && !state.customContactSaved) return;
    swal(
      {
        title: 'Record Showing',
        text: `Would you like to record this showing on ${
          state.appointment.date
        } from ${state.appointment.time.format(
          'h:mm a',
        )} to ${state.appointment.timeEnd.format('h:mm a')}?`,
        html: true,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        closeButtonText: 'Cancel',
        closeOnConfirm: true,
      },
      () => {
        const tempAppointment = _.cloneDeep(state.appointment);
        if (state.customContact) {
          tempAppointment.customContact = state.customContact;
          tempAppointment.notify = {
            text: false,
            email: false,
            office: true,
          };
        }
        updateState(draft => {
          draft.dataLoaded = false;
          if (draft.customContact) {
            draft.appointment.customContact = draft.customContact;
            draft.appointment.notify = {
              text: false,
              email: false,
              office: true,
            };
          }
        });

        AppointmentBookService.recordAppointment({
          ...tempAppointment,
          buyers: inviteBuyers.map(buyer => buyer.value),
        }).then(appointment => {
          $location.path(`/listing/${appointment.listingId}/view`);
        });
      },
    );
  };

  function resetAppointment() {
    updateState(draft => {
      draft.appointment = {
        type:
          draft.bookFor === 'nonAgent'
            ? draft.nonAgentTypes[0]
            : draft.appointmentTypes[0],
        listingId: $routeParams.listingId,
        notify: {
          text: false,
          email: true,
          office: false,
        },
      };
    });
  }

  const customContactEdit = () => {
    updateState(draft => {
      draft.customContactSaved = false;
    });
  };

  const customContactDelete = () => {
    const nonAgentType = {
      Photographer: 'Photographer',
      'Pest Control': 'Pest Control',
      Appraisal: 'Appraiser',
      'Home Inspection': 'Inspector',
    }[state.appointment.type];
    swal(
      {
        title: `Delete ${nonAgentType}`,
        text: `Are you sure you want to delete this ${nonAgentType.toLowerCase()}?\nThis operation is permanent and cannot be undone`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'Yes',
        closeOnConfirm: true,
      },
      () => {
        updateState(draft => {
          draft.dataLoaded = false;
        });
        AppointmentBookService.deleteNonAgent(state.nonAgent.id)
          .then(() => {
            changeBookFor('nonAgent');
            updateState(draft => {
              draft.dataLoaded = true;
            });
          })
          .catch(err => {
            ErrorService.toastyHTTPErr(err);
            updateState(draft => {
              draft.dataLoaded = true;
            });
          });
      },
    );
  };

  const customAgentDelete = () => {
    swal(
      {
        title: `Delete Custom Agent`,
        text: `Are you sure you want to delete this custom agent?\nThis operation is permanent and cannot be undone`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'Yes',
        closeOnConfirm: true,
      },
      () => {
        updateState(draft => {
          draft.dataLoaded = false;
        });
        AppointmentBookService.deleteCustomAgent(state.customAgent.id)
          .then(() => {
            changeBookFor('others');
            updateState(draft => {
              draft.dataLoaded = true;
            });
          })
          .catch(err => {
            ErrorService.toastyHTTPErr(err);
            updateState(draft => {
              draft.dataLoaded = true;
            });
          });
      },
    );
  };

  const deleteCustomAgentOrContact = () =>
    state.customAgent.id ? customAgentDelete() : customContactDelete();

  function bookAppointment(confirm, { timeRestrictionsOverridden } = {}) {
    updateState(draft => {
      if (draft.isMobile) {
        draft.showLoadingSpinnerInButton = true;
      } else {
        draft.dataLoaded = false;
      }
    });

    let redirectAfterTimeChange;
    if (state.isTimeChange) {
      redirectAfterTimeChange = () => {
        const params = new URLSearchParams($location.search());
        const redirectTo = params.get('redirectTo');
        let path = '/admin/dashboard';
        if (isAssumeOrgType($rootScope.auth?.org?.type)) {
          path = `/answering/queue`;
        }
        if (confirm) {
          path = `/appointments/${$routeParams.appointmentId}/action`;
        }
        if (state.autoConfirm || !state.isAdmin) {
          path = `/listing/${state.listing.id}/view`;
        }
        if ($location.path().startsWith('/my_business/appointment')) {
          path = `/my_business/appointment/${$routeParams.appointmentId}`;
        }
        if ($location.path().startsWith('/my_business/showing')) {
          path = `/my_business/showing/${$routeParams.appointmentId}`;
        }
        if (redirectTo === 'calendar') {
          $location.search('redirectTo', null);
          $location.search(
            'toast',
            isListingContact
              ? 'time_change_suggested'
              : 'time_change_requested',
          );
          path = `/my_business/showings/${
            isListingContact ? 'listing' : 'buyer'
          }`;
        }
        if (redirectTo === 'listingCalendar') {
          $location.search('redirectTo', null);
          // Only redirect to listing calendar if Book & Confirm was not selected
          // and user is not an admin or not the LA or listing contact
          const isAdminAndNonContact =
            state.isAdmin && !state.isListingAgent && !state.isListingHiddenRep;
          if (!confirm && !isAdminAndNonContact) {
            $location.search(
              'toast',
              isListingContact
                ? 'time_change_suggested'
                : 'time_change_requested',
            );
            path = `/listing/${appointmentDetails.appointment.listing.id}/showings`;
          }
        }
        $location.path(path);
      };

      AppointmentBookService.changeTime(state.appointment, {
        timeRestrictionsOverridden,
        isListingContact,
        keepOriginalAppointment: state.keepOriginalAppointment,
      })
        .then(() => {
          if (state.isMobile) {
            updateState(draft => {
              draft.showLoadingSpinnerInButton = false;
            });

            return new Promise(resolve => {
              swal(
                {
                  title: 'Time Changed',
                  text: 'The appointment time has been changed.',
                  type: 'success',
                },
                resolve,
              );
            });
          }
        })
        .then(redirectAfterTimeChange)
        .catch(err => {
          updateState(draft => {
            draft.showLoadingSpinnerInButton = false;
            draft.dataLoaded = true;
          });

          ErrorService.toastyHTTPErr(err);
        })
        .finally(() => setIsSubmitting(false));
      return;
    }
    const tempAppointment = _.cloneDeep(state.appointment);
    if (state.customContact) {
      tempAppointment.customContact = state.customContact;
      tempAppointment.notify = {
        text: false,
        email: false,
        office: true,
      };
      updateState(draft => {
        draft.appointment.customContact = draft.customContact;
        draft.appointment.notify = {
          text: false,
          email: false,
          office: true,
        };
      });
    }

    AppointmentBookService.saveAppointment(
      {
        ...tempAppointment,
        buyers: inviteBuyers.map(buyer => buyer.value),
      },
      timeRestrictionsOverridden,
    )
      .then(savedAppointment => {
        if (state.isMobile) {
          updateState(draft => {
            draft.showLoadingSpinnerInButton = false;
          });
        }

        message.success({ content: 'Showing booked' });
        return savedAppointment;
      })
      .then(apt => {
        let path = `/listing/${apt.listingId}/view`;
        if (confirm) {
          path = `/appointments/${apt.id}/action`;
        } else if (state.autoConfirm || partialAutoConfirm) {
          if (apt.isPending && state.isAdmin) {
            path = `/appointments/${apt.id}/confirm`;
          }
        }
        $location.path(path);
      })
      .catch(err => {
        updateState(draft => {
          draft.showLoadingSpinnerInButton = false;
          draft.dataLoaded = true;
        });

        if (
          err.status === 403 &&
          err.data.message?.includes(
            'Oops, you can’t book a showing on this listing as it has been updated. Please book a showing on the new listing. URL: ',
          )
        ) {
          const hyperlinkURL = err.data.message.split('URL: ')[1];
          message.warning({
            content: (
              <Text>
                Oops, you can’t book a showing on this listing as it has been
                updated. Please{' '}
                <a href={hyperlinkURL}>book a showing on the new listing.</a>
              </Text>
            ),
            style: { zIndex: '10' },
          });
          return;
        }

        ErrorService.toastyHTTPErr(err);
      })
      .finally(() => setIsSubmitting(false));
  }

  const isResidentialTypeListing = ['condo', 'freehold', 'income'].includes(
    state.listing?.type,
  );

  const isBuyerShowing =
    ['Buyer/Broker', 'Buyer Visit', 'Revisit', 'Dual Agency'].includes(
      state.appointment.type,
    ) &&
    (state.appointment.coop ||
      (!_.isEmpty(state.customAgent) && state.customContactSaved));

  const shouldSendModal =
    featureEnabled('narBuyerSettlementFeature') &&
    hasNarBuyerSettlement &&
    !state.isTimeChange &&
    isBuyerShowing &&
    isResidentialTypeListing &&
    !hasBuyerAgreed;

  function submitAppointment(
    confirm,
    // should be only used when submitAppointment is called recursively
    bypassBuyerAgreement,
  ) {
    setIsSubmitting(true);

    if (state.customContact && !state.customContactSaved) {
      return;
    }

    if (shouldSendModal && !bypassBuyerAgreement) {
      showBuyerAgreementModal({
        onOk: () => submitAppointment(confirm, true),
      });
      setIsSubmitting(false);
      return;
    }

    if (state.invalidTimeslot && (state.isAdmin || state.isListingAgent)) {
      swal(
        {
          title: 'Restricted time slot',
          text: 'You have selected a restricted time slot. Are you sure?',
          html: true,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          closeButtonText: 'Cancel',
          closeOnConfirm: true,
        },
        () => {
          bookAppointment(confirm, { timeRestrictionsOverridden: true });
        },
      );
      setIsSubmitting(false);
    } else {
      bookAppointment(confirm, {});
    }
  }

  const resetNotificationSettings = () => {
    updateState(draft => {
      draft.appointment.notify = {
        text: false,
        email: true,
        office: false,
      };
    });
  };

  const selectSearchRoster = () => {
    updateState(draft => {
      draft.customContact = null;
      draft.nonAgent = {};
      draft.customContactSaved = false;
      draft.appointment.type =
        draft.bookFor === 'nonAgent' ? null : draft.appointmentTypes[0];
      draft.nextStepLabel =
        draft.bookFor === 'nonAgent' || draft.bookFor === 'others'
          ? 'Save Profile'
          : 'Next >';
      draft.appointment.notify =
        draft.bookFor === 'nonAgent'
          ? {
              text: false,
              email: false,
              office: true,
            }
          : {
              text: false,
              email: true,
              office: false,
            };
    });
    resetDate();
  };

  function changeBookFor(type) {
    updateState(draft => {
      draft.bookFor = type;
    });
    resetDate();
    resetNotificationSettings();
    if (type === 'self') {
      populateInternalAppointment($rootScope.auth);
    } else {
      selectSearchRoster();
      updateState(draft => {
        draft.appointment.coop = null;
        draft.coopUserOrg = null;
        draft.selectedUser = null;
      });
    }
  }

  // Agent/contact selection
  const deselectUser = () => {
    updateState(draft => {
      draft.selectedUser = null;
      draft.appointment.coop = null;
    });
    resetAppointment();
  };

  const deselectNonAgent = () => {
    updateState(draft => {
      draft.selectedUser = null;
      draft.customContact = null;
      draft.nonAgent = {};
      draft.appointment.coop = null;
    });
    resetAppointment();
  };

  const selectUser = async user => {
    updateState(draft => {
      draft.appointment.coop = {};
      draft.nonAgent = {};
      draft.customContact = null;
    });

    resetDate();

    try {
      const foundUser = await UserController.searchDetails({
        ...user,
        reason: 'ViewingPreSale',
        listingId: state.listingId || state.listing?.id,
      });
      foundUser.uid ||= user.uid;
      foundUser.mlsType ||= user.mlsType;
      updateState(draft => {
        draft.selectedUser = foundUser;
        [draft.selectedEmail] = draft.selectedUser.emails;
        draft.appointment.coop = {
          uid: foundUser.uid,
          id: foundUser.id,
          email: foundUser.emails?.[0] || foundUser.email || '',
          name: `${foundUser.firstName} ${foundUser.lastName}`,
          firstName: foundUser.firstName,
          lastName: foundUser.lastName,
          text: foundUser.text,
          board: foundUser.board || '',
          boardId: foundUser.boardId,
          mls: foundUser.mlsType,
          orgFeatures: foundUser.org?.features,
        };
        draft.coopUserOrg = foundUser.org;
        if (user.id) {
          draft.appointment.coop.phone = foundUser.phone;
        }
        if (foundUser.lastPhone) {
          draft.appointment.coop.phone = foundUser.lastPhone;
        }
      });
    } catch (err) {
      // TOAST
      ErrorService.toastyHTTPErr(err);
    }
  };

  const selectNonAgent = nonagent => {
    updateState(draft => {
      draft.appointment.coop = {};
      draft.nonAgent = {};
      draft.customContact = null;
      draft.customAgent = {};
    });
    resetDate();
    AppointmentBookService.getNonAgentDetails(nonagent)
      .then(foundNonAgent => {
        updateState(draft => {
          draft.selectedUser = null;
          draft.appointment.type = foundNonAgent.type;

          draft.customContact = {
            nonAgentId: foundNonAgent.id,
            isNotAgent: true,
            firstName: foundNonAgent.firstName,
            lastName: foundNonAgent.lastName,
            email: foundNonAgent.email,
            phone: foundNonAgent.phone,
            org: {
              name: foundNonAgent.companyName,
              phone: foundNonAgent.companyPhone,
              address: foundNonAgent.companyAddress,
            },
          };
          draft.nonAgentId = foundNonAgent.id;
          draft.nonAgent = {
            id: foundNonAgent.id,
            firstName: foundNonAgent.firstName,
            lastName: foundNonAgent.lastName,
            type: foundNonAgent.type,
            email: foundNonAgent.email,
            phone: foundNonAgent.phone,
            companyName: foundNonAgent.companyName,
            companyPhone: foundNonAgent.companyPhone,
            companyAddress: foundNonAgent.companyAddress,
            onboardingPortalId: foundNonAgent.onboardingPortalId,
            orgId: foundNonAgent.org,
          };

          draft.customContactSaved = true;
          draft.customContactPreApproved = Boolean(foundNonAgent.preApproved);
        });
      })
      .catch(err => {
        // TOAST
        ErrorService.toastyHTTPErr(err);
      });
  };

  const selectCustomAgent = async customagent => {
    const { id: customAgentId } = customagent;
    updateState(draft => {
      draft.appointment.coop = {};
      draft.nonAgent = {};
      draft.customContact = null;
      draft.customAgent = {};
    });

    const foundCustomAgent =
      await AppointmentBookService.getCustomAgent(customAgentId);
    updateState(draft => {
      draft.selectedUser = null;
      draft.customContact = {
        firstName: foundCustomAgent.firstName,
        lastName: foundCustomAgent.lastName,
        email: foundCustomAgent.email,
        phone: foundCustomAgent.phone,
        org: {
          name: foundCustomAgent.companyName,
          phone: foundCustomAgent.companyPhone,
          address: foundCustomAgent.companyAddress,
        },
        customAgentId: foundCustomAgent.id,
      };
      draft.customAgent = {
        id: foundCustomAgent.id,
        firstName: foundCustomAgent.firstName,
        lastName: foundCustomAgent.lastName,
        email: foundCustomAgent.email,
        phone: foundCustomAgent.phone,
        companyName: foundCustomAgent.companyName,
        companyPhone: foundCustomAgent.companyPhone,
        companyAddress: foundCustomAgent.companyAddress,
        createdBy: foundCustomAgent.createdBy,
      };
      draft.customAgentId = foundCustomAgent.id;
      draft.customContactSaved = true;
    });

    resetDate();
  };

  const selectCustomContact = () => {
    updateState(draft => {
      draft.nonAgent = {};
      draft.customContact = {};
      draft.customAgent = {};
      draft.customContactSaved = false;
      draft.appointment.type =
        draft.bookFor === 'nonAgent' ? null : draft.appointmentTypes[0];
      draft.selectedUser = null;
      draft.appointment.notify = {
        text: false,
        email: false,
        office: true,
      };
      draft.appointment.coop = null;
      draft.nextStepLabel = 'Save Profile';
    });
    resetDate();
  };

  function timePassed(time) {
    const mins =
      Math.ceil(moment().minutes() / state.interval) * state.interval;
    return time.unix() < moment().minutes(mins).startOf('minute').unix();
  }

  const startRecord = () =>
    Swal.fire({
      title: 'RECORD SHOWING',
      text: "You've selected a date in the past, would you like to record a past showing?",
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

  const startComingSoonBooking = () =>
    Swal.fire({
      title: 'BOOK SHOWING',
      text: `This listing will be active on ${formatDate(
        new Date(state.listing.coming_soon_date),
        'MMM d, yyyy',
      )}. Are you sure you'd like to book a showing before it goes live?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

  useEffect(() => {
    if (state.listing?.openHouses) {
      updateDate(state.appointment.date);
    }
  }, [state.listing]);

  useEffect(() => {
    setInviteBuyers([]);
  }, [state.selectedUser]);

  function updateDate(date) {
    const isDayInPast = AppointmentBookService.isDayInPast(date);
    if (isDayInPast && (state.isAdmin || state.isListingAgent)) {
      if (state.showPastDateWarning) {
        startRecord().then(result => {
          if (result.value) {
            updateState(draft => {
              draft.appointment.recordAppointment = true;
              draft.selecteddate = date;
              draft.showPastDateWarning = false;
            });
            doUpdateDate(date);
          } else {
            resetDate();
          }
        });
      }

      updateState(draft => {
        draft.showPastDateWarning = true;
      });
    }

    const isComingSoonListing = state.listing?.status === 'Coming Soon';
    const isBeforeComingSoonDate = () =>
      date <
      getStartOfDayInTimezone(
        state.listing.coming_soon_date,
        state.listing.timezone,
      );

    if (
      !isDayInPast &&
      isComingSoonListing &&
      isBeforeComingSoonDate() &&
      (state.isAdmin || state.isListingAgent)
    ) {
      if (state.showComingSoonWarning) {
        startComingSoonBooking().then(result => {
          if (result.value) {
            updateState(draft => {
              draft.appointment.recordAppointment = false;
              draft.selecteddate = date;
              draft.showComingSoonWarning = false;
            });
            doUpdateDate(date);
          } else {
            resetDate();
          }
        });
      }

      updateState(draft => {
        draft.showComingSoonWarning = true;
      });
    }

    if (!isDayInPast) {
      updateState(draft => {
        draft.appointment.recordAppointment = false;
      });
    }

    doUpdateDate(date);
  }

  function doUpdateDate(date) {
    // Avoid updating the date if it's the same as the current date, or no appointment is available, or if undefined date is passed in
    if (
      (prevDate && moment(prevDate).isSame(date)) ||
      _.isEmpty(state.appointment) ||
      !date
    )
      return;

    setPrevDate(date);

    const dateStr = moment(date).format('YYYY-MM-DD');

    const startOfDay = moment
      .tz(dateStr, state.listing?.timezone)
      .startOf('day');
    const endOfDay = moment.tz(dateStr, state.listing?.timezone).endOf('day');
    const timeOfDay = startOfDay.clone();
    const timesOfDay = [];
    while (timeOfDay.unix() < endOfDay.unix()) {
      timesOfDay.push(timeOfDay.clone());
      timeOfDay.add(state.interval, 'm');
    }
    updateState(draft => {
      draft.timesOfDay = timesOfDay;
      draft.appointment.date = dateStr;
      draft.appointment.time = null;
      draft.selectedTimes = {};
      draft.invalidTimeslot = false;
    });

    if (
      (_.includes(state.unavailableDays, dateStr) &&
        !state.isAdmin &&
        !state.isListingAgent &&
        !state.isTeamAdmin) ||
      (state.unavailableAfter &&
        moment(date).unix() * 1000 >= state.unavailableAfter.getTime())
    ) {
      updateState(draft => {
        draft.dateNotAvailable = true;
      });
    } else {
      updateState(draft => {
        draft.dateNotAvailable = false;
        draft.loadingTimes = true;
      });
      if ((state.listing && state.listing.id) || state.listingId) {
        appointmentInfo(dateStr).then(data => {
          let earliestAvailableTime;
          updateState(draft => {
            draft.availableTimes = { ...data };
            draft.unavailableTimes = Object.keys(data).map(key => {
              if ({}.hasOwnProperty.call(data, key) && !data[key]) {
                return key;
              }
              const availableTime = moment(key, 'h:mm A');
              const dateTime = moment
                .tz(draft.appointment.date, draft.listing?.timezone)
                .hour(availableTime.hour())
                .minute(availableTime.minute());
              if (!earliestAvailableTime && !timePassed(dateTime))
                earliestAvailableTime = key;
              return undefined;
            });
            draft.earliestAvailableTime = earliestAvailableTime;
          });

          if (earliestAvailableTime) {
            scrollToEarliestAvailableTime(earliestAvailableTime);
          }
          updateState(draft => {
            draft.loadingTimes = false;
          });
        });
      }
    }
    updateState(draft => {
      delete draft.lastTimeStr;
      draft.appointmentTimes = {};
      draft.hiddenTimes = {};
      draft.appointmentsInfo = {};
      draft.openHouses = {};
      draft.openHouseTimes = {};
    });

    state.allAppointments.forEach(({ appointment }) => {
      const startTime = moment.tz(appointment.time, state.listing.timezone);
      if (
        startOfDay.unix() <= startTime.unix() &&
        startTime.unix() < endOfDay.unix()
      ) {
        updateState(draft => {
          if (!draft.appointmentsInfo[startTime.format('h:mm A')]) {
            draft.appointmentsInfo[startTime.format('h:mm A')] = {
              appointments: [],
            };
          }
          draft.appointmentsInfo[startTime.format('h:mm A')].appointments.push(
            appointment,
          );
        });
      }
      const time = moment.tz(
        moment.max(moment(appointment.time), startOfDay),
        state.listing.timezone,
      );
      const timeEnd = moment.tz(
        moment.min(moment(appointment.timeEnd), endOfDay),
        state.listing.timezone,
      );
      const addTime = time.clone();
      updateState(draft => {
        while (addTime.unix() < timeEnd.unix()) {
          draft.appointmentTimes[addTime.format('h:mm A')] = true;
          addTime.add(draft.interval, 'm');
        }
      });
    });

    if (state.listing?.openHouses) {
      const openHouseTimes = {};
      for (let i = 0; i < state.listing.openHouses.length; i += 1) {
        const openHouse = state.listing.openHouses[i];
        const ohDate = moment(openHouse.timeFrom).format('YYYY-MM-DD');

        if (dateStr === ohDate) {
          const time = moment.tz(openHouse.timeFrom, state.listing.timezone);
          const timeEnd = moment.tz(openHouse.timeTo, state.listing.timezone);
          time.minute(
            Math.round(time.minute() / state.interval) * state.interval,
          );
          timeEnd.minute(
            Math.round(timeEnd.minute() / state.interval) * state.interval,
          );
          const addTime = time.clone();

          while (addTime.unix() <= timeEnd.unix()) {
            openHouseTimes[addTime.format('h:mm A')] = true;
            addTime.add(state.interval, 'm');
          }
          updateState(draft => {
            addTime.subtract(state.interval, 'm');
            draft.openHouses[addTime.format('h:mm A')] = true;
          });
        }
      }

      if (Object.keys(openHouseTimes).length) {
        updateState(draft => {
          draft.openHouseTimes = openHouseTimes;
        });
      }
    }
    return undefined;
  }

  function scrollToEarliestAvailableTime(earliestAvailableTime) {
    // Check data is loaded and view is rendered.
    /* If Data is not loaded
    useEffect will call scrollToearliest available time when data is loaded
    */
    if (state.dataLoaded) {
      const id = $location.hash();
      $location.hash(`timeWidget-${earliestAvailableTime}`);
      $anchorScroll();
      $location.hash(id);
    } else {
      updateState(draft => {
        draft.shouldScroll = true;
      });
    }
  }

  function setDuration(newDuration) {
    if (!state.appointment.time || !state.appointment.time.clone) {
      updateState(draft => {
        draft.invalidTimeslot = false;
      });
      return;
    }
    const time = state.appointment.time.clone();
    const timeEnd = time.clone().add(newDuration, 'm');

    const addTime = time.clone();
    const selectedTimes = {};
    while (addTime.unix() < timeEnd.unix()) {
      selectedTimes[addTime.format('h:mm A')] = true;
      addTime.add(state.interval, 'm');
    }
    updateState(draft => {
      draft.selectedTimes = selectedTimes;
      draft.appointment.timeEnd = timeEnd;
      draft.invalidTimeslot =
        _.intersection(draft.unavailableTimes, Object.keys(selectedTimes))
          .length > 0;
    });
  }

  const selectTime = time => {
    if (timePassed(time) && (state.isAdmin || state.isListingAgent)) {
      if (state.isTimeChange) {
        return;
      }
      if (!state.appointment.recordAppointment) {
        if (state.showPastDateWarning) {
          startRecord().then(result => {
            if (result.value) {
              updateState(draft => {
                draft.appointment.recordAppointment = true;
                draft.selecteddate = date;
                draft.showPastDateWarning = false;
              });
              doUpdateDate(date);
            } else {
              resetDate();
            }
          });
        }

        updateState(draft => {
          draft.showPastDateWarning = true;
        });
      }
    } else {
      updateState(draft => {
        draft.appointment.recordAppointment = false;
      });
    }
    updateState(draft => {
      draft.customDuration.enabled = false;
      draft.appointment.time = time;
    });
  };

  /**
   * @param {object} aptConfigDays - appointmentConfig.days {'FRI':{'11:00':'Available'}}
   * @param {object} aptConfigSpecialTimes - appointmentConfig.specialTimes {'2022-10-14':{'11:00':'Available'}}
   * @param {object} selectedTimes - selectedTimes {'10:00 AM': true}
   * @param {string} selectedDay - selectedDay 'FRI'
   * @returns {boolean} boolean
   */
  function checkPartialAutoConfirm(
    aptConfigDays,
    aptConfigSpecialTimes,
    selectedTimes,
    selectedDay,
  ) {
    const day = moment(selectedDay).format('ddd').toUpperCase();
    const specialDay = moment(selectedDay).format('YYYY-MM-DD');
    const selectedTimesArr = Object.keys(selectedTimes);

    // Rounding down to nearest 30 mins to match selectedtimes with the times in aptconfigdays
    const filteredArr = selectedTimesArr
      .map(timeslot => {
        const time = moment(timeslot, 'HH:mm a');
        if (time.format('mm') === '15' || time.format('mm') === '45') {
          time.subtract(15, 'm');
        }
        return time.format('H:mm');
      })
      .filter(
        time =>
          aptConfigDays[day][time] === 'AutoConfirm' ||
          aptConfigSpecialTimes?.[specialDay]?.[time] === 'AutoConfirm',
      );
    return selectedTimesArr.length === filteredArr.length;
  }

  function setCustomDuration() {
    const customDuration =
      Number(state.customDuration.durationHour) * 60 +
      Number(state.customDuration.durationMinutes);
    setDuration(customDuration);
  }

  function enableCustomDuration(customDuration) {
    updateState(draft => {
      draft.customDuration.enabled = customDuration;
    });
    if (customDuration) {
      setCustomDuration();
    } else {
      setDuration(state.duration);
    }
  }

  const expandTime = (event, timeStr, expand) => {
    const { timezone } = state.listing;
    if (expand) {
      setDuration(0);
      if (state.lastTimeStr) expandTime(event, state.lastTimeStr, false);
      updateState(draft => {
        draft.selectedTimes = {};
        draft.appointment.time = null;
        draft.lastTimeStr = timeStr;
      });
    }
    event.stopPropagation();
    let maxCount = 0;
    const startOfDay = moment
      .tz(state.appointment.date, timezone)
      .startOf('day');

    const endOfDay = moment.tz(state.appointment.date, timezone).endOf('day');
    let maxTimeEnd = startOfDay.clone();

    state.appointmentsInfo[timeStr].appointments.forEach(appointment => {
      const time = moment.tz(
        moment.max(moment(appointment.time), startOfDay),
        timezone,
      );
      const timeEnd = moment.tz(
        moment.min(moment(appointment.timeEnd), endOfDay),
        timezone,
      );
      const addTime = time.clone();
      let count = 1;
      addTime.add(state.interval, 'm');
      const hiddenTimes = {};
      while (addTime.unix() < timeEnd.unix()) {
        count += 1;
        hiddenTimes[addTime.format('h:mm A')] = expand;
        addTime.add(state.interval, 'm');
      }
      updateState(draft => {
        draft.hiddenTimes = hiddenTimes;
      });
      maxCount = Math.max(count, maxCount);
      maxTimeEnd = moment.tz(
        moment.max(maxTimeEnd, moment(appointment.timeEnd)),
        timezone,
      );
    });
    updateState(draft => {
      draft.appointmentsInfo[timeStr].expand = expand
        ? { 'min-height': `${maxCount * 43}px` }
        : undefined;
      draft.appointmentsInfo[timeStr].maxTimeEnd = maxTimeEnd;
    });
  };

  const getPrettyDate = date => moment(date).format('MMM DD, YYYY');

  const hasValidTime = () => {
    // These users should be able to book regardless of restrictions
    if (state.isAdmin || state.isListingAgent || state.isTeamAdmin) return true;

    // If the date is completely restricted from the weekly time selector,
    // `vm.dateNotAvailable` is true and we should disallow booking
    if (state.dateNotAvailable) return false;

    // Otherwise, we need to check availabilities defined by existing
    // appointments and the special day settings
    const result = Object.entries(state.availableTimes || { 0: true })
      .filter(([key]) => !key.startsWith('$')) // Filter out promise-related keys
      .some(([, isAvailable]) => isAvailable);
    return result;
  };

  function moveToStep(step) {
    if (step < 0) return clearSelected();
    setCurrentStep(step);
    if (step === state.mobileWorkflowSteps.selectTimeStep) {
      updateState(draft => {
        draft.shouldScroll = true;
      });
    }
  }

  const moveToNextStep = () => {
    moveToStep(currentStep + 1);
  };

  const moveToPreviousStep = () => {
    moveToStep(currentStep - 1);
  };

  const clearSelected = () => {
    if (showingOverrideType) return setShowingOverrideType(null);
    updateState(draft => {
      draft.selectedAppointment = null;
    });
  };

  const addPropertyToTour = async (
    listingId,
    virtualTourId,
    invitees,
    bypassBuyerAgreement,
  ) => {
    if (shouldSendModal && !bypassBuyerAgreement) {
      showBuyerAgreementModal({
        onOk: () => addPropertyToTour(listingId, virtualTourId, invitees, true),
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.post(`/virtual_tour/${virtualTourId}/property_to_tour/add`, {
        listingId,
        invitees,
      });
      const path = `/listing/${state.listing.id}/tour/${state.selectedTour.id}/details`;

      $rootScope.$apply(() => {
        $location.path(path);
      });
    } catch (err) {
      errorReporter.error(`Error adding property to virtual tour: ${err}`);
    }
  };

  const buildPage = () => (
    <>
      <AppointmentHeader
        appointment={state.appointment}
        auth={state.auth}
        autoConfirm={state.autoConfirm}
        invalidTimeslot={state.invalidTimeslot}
        isAdmin={state.isAdmin}
        isListingAgent={state.isListingAgent}
        isListingContact={isListingContact}
        isMls={false}
        isTeamAdmin={state.isTeamAdmin}
        isTimeChange={state.isTimeChange}
        isMobile={state.isMobile}
        isBookingVirtualTour={state.isBookingVirtualTour}
        selectedTour={state.selectedTour}
        invitees={state.invitees}
        listing={state.listing}
        listingId={state.listingId}
        savedAppointment={state.savedAppointment}
        showError={state.showError}
        orgDetails={state.orgDetails}
        partialAutoConfirm={partialAutoConfirm}
        isSubmitting={isSubmitting}
        customDuration={state.customDuration}
        showingOverrideType={showingOverrideType}
        // functions
        setIsSubmitting={setIsSubmitting}
        setHasMobileNumber={setHasMobileNumber}
        addPropertyToTour={addPropertyToTour}
        updateState={updateState}
        submitAppointment={submitAppointment}
        clearSelected={clearSelected}
        recordAppointment={recordAppointment}
        // angular/services
        errorReporter={errorReporter}
        ModalService={ModalService}
        $location={$location}
        $rootScope={$rootScope}
      />

      {!loading && !listingLoading && state.listing && state.listing.id && (
        <MainContainer
          isMobile={state.isMobile}
          mobileContentHeight={state.mobileContentHeight}
        >
          <div className={`row ${styles.visibleBlockOnMobile}`}>
            <div className="col-xs-12">
              <h5>
                <span>
                  <AppointmentSubHeader
                    isTimeChange={state.isTimeChange}
                    isListingContact={isListingContact}
                    isMobile={state.isMobile}
                  />
                </span>
              </h5>
              <h3 className={styles.address}>
                <strong>
                  {state.listing && state.listing.line1}{' '}
                  {state.listing && state.listing.line2}
                </strong>
              </h3>
            </div>
          </div>

          {!showingOverrideType &&
          getShowingScreenType(
            state.listing?.canEdit,
            state.listing?.availability,
            state.noMoreShowings,
          ) !== ShowingScreenType.Default ? (
            <AppointmentOverride
              listing={state.listing}
              handleOverride={setShowingOverrideType}
              selectedOverrideType={prevShowingOverrideType}
              noMoreShowings={state.noMoreShowings}
              isExternal={false}
              updateState={updateState}
              orgDetails={state.orgDetails}
              availability={
                $rootScope.listingAvailabilityRelabel?.[
                  state.listing?.availability
                ] || state.listing?.availability
              }
              setIsBookingAllowed={setIsBookingAllowed}
            />
          ) : (
            <div className={`row ${styles.flexGrow}`}>
              <div className="col-md-12">
                <div className="row">
                  {state.isVirtualTourEnabled && !state.isTimeChange && (
                    <VirtualTourTab
                      isMobile={state.isMobile}
                      setCurrentStep={setCurrentStep}
                      updateState={updateState}
                      hasVSFeatureFlag={hasFeature('VIRTUAL_SHOWINGS')}
                    />
                  )}
                  <PreBookingInfoAlert state={state} />
                  <Step1User
                    {...state}
                    isListingContact={isListingContact}
                    isUnverified={$rootScope.auth.isUnverified}
                    updateState={updateState}
                    currentStep={currentStep}
                    selectUser={selectUser}
                    deselectUser={deselectUser}
                    selectNonAgent={selectNonAgent}
                    deselectNonAgent={deselectNonAgent}
                    selectCustomAgent={selectCustomAgent}
                    changeBookFor={changeBookFor}
                    selectSearchRoster={selectSearchRoster}
                    selectCustomContact={selectCustomContact}
                    selectNotifyMethod={selectNotifyMethod}
                    resetDate={resetDate}
                    isValidContact={isValidContact}
                    customContactEdit={customContactEdit}
                    deleteCustomAgentOrContact={deleteCustomAgentOrContact}
                    $rootScope={$rootScope}
                    AppointmentBookService={AppointmentBookService}
                    ErrorService={ErrorService}
                    hasMobileNumber={hasMobileNumber}
                    setHasMobileNumber={setHasMobileNumber}
                    isLicensed={$rootScope.auth?.isLicensed}
                    isBookingVirtualTour={state.isBookingVirtualTour}
                    haveListingPermission={state.haveListingPermission}
                    isListingRep={state.isListingRep}
                    showingOverrideType={showingOverrideType}
                    inviteBuyers={inviteBuyers}
                    setInviteBuyers={setInviteBuyers}
                  />

                  {/* Admin listing preview */}
                  {state.isAdmin &&
                    state.haveListingPermission &&
                    state.dataLoaded &&
                    !state.isBookingVirtualTour &&
                    !state.isTimeChange &&
                    !(state.appointment.coop || state.customContactSaved) && (
                      <div className="col-md-8 hidden-xs">
                        <ListingPreview
                          listingId={state.listingId}
                          listing={state.listing}
                          appointmentConfig={state.appointmentConfig}
                          offerDetails={state.offerDetails}
                        />
                      </div>
                    )}

                  {/* Step 2 / 3 AptBooking or Virtual Tour */}
                  {((state.appointment && state.appointment.coop) ||
                    state.customContactSaved) && (
                    <>
                      {state.isBookingVirtualTour ? (
                        <VtAppointmentBooking
                          listingId={state.listing.id}
                          coop={{
                            ...state.appointment?.coop,
                            org: state.coopUserOrg,
                          }}
                          currentStep={currentStep}
                          selectedTour={state.selectedTour}
                          updateState={updateState}
                        />
                      ) : (
                        <Step23AppointmentBooking
                          state={state}
                          isListingContact={isListingContact}
                          currentStep={currentStep}
                          updateState={updateState}
                          updateDate={updateDate}
                          $rootScope={$rootScope}
                          UtilService={UtilService}
                          partialAutoConfirm={partialAutoConfirm}
                          selectTime={selectTime}
                          setDuration={setDuration}
                          moveToStep={moveToStep}
                          setCustomDuration={setCustomDuration}
                          enableCustomDuration={enableCustomDuration}
                          hasValidTime={hasValidTime}
                          getPrettyDate={getPrettyDate}
                          timePassed={timePassed}
                          expandTime={expandTime}
                        />
                      )}
                    </>
                  )}
                  {state.isMobile && (
                    <Step4Mobile
                      appointment={state.appointment}
                      listing={state.listing}
                      originalAppointment={state.originalAppointment}
                      keepOriginalAppointment={state.keepOriginalAppointment}
                      appointmentTypes={state.appointmentTypes}
                      bookFor={state.bookFor}
                      coopUserOrg={state.coopUserOrg}
                      currentStep={currentStep}
                      isMls={false}
                      isMobile={state.isMobile}
                      isListingContact={isListingContact}
                      isTimeChange={state.isTimeChange}
                      mobileWorkflowSteps={state.mobileWorkflowSteps}
                      sameOrg={state.sameOrg}
                      selectedUser={state.selectedUser}
                      updateState={updateState}
                      selectNotifyMethod={selectNotifyMethod}
                      moveToStep={moveToStep}
                      hasMobileNumber={hasMobileNumber}
                      setHasMobileNumber={setHasMobileNumber}
                      showingOverrideType={showingOverrideType}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {
            // if mobile, only show on last stage
            // obs: last stage for virtual tours seems to be 2
            !(
              state.isMobile &&
              ((currentStep < 3 && !state.isBookingVirtualTour) ||
                currentStep < 2)
            ) &&
              // Buyer Agreement is hidden for modify or suggested time change flows
              !state.isTimeChange &&
              isBuyerShowing &&
              isResidentialTypeListing &&
              isBookingAllowed &&
              hasNarBuyerSettlement && <BuyerAgreementCheckbox />
          }

          {state.isMobile && isBookingAllowed && (
            <MobileButtons
              isBookingVirtualTour={state.isBookingVirtualTour}
              noMoreShowings={state.noMoreShowings}
              isMls={false}
              currentStep={currentStep}
              mobileWorkflowSteps={state.mobileWorkflowSteps}
              listing={state.listing}
              appointment={state.appointment}
              customContactSaved={state.customContactSaved}
              dateNotAvailable={state.dateNotAvailable}
              isAdmin={state.isAdmin}
              isListingContact={isListingContact}
              isTimeChange={state.isTimeChange}
              isListingAgent={state.isListingAgent}
              invalidTimeslot={state.invalidTimeslot}
              showLoadingSpinnerInButton={state.showLoadingSpinnerInButton}
              selectedTour={state.selectedTour}
              invitees={state.invitees}
              isSubmitting={isSubmitting}
              showingOverrideType={showingOverrideType}
              // functions
              setIsSubmitting={setIsSubmitting}
              setHasMobileNumber={setHasMobileNumber}
              addPropertyToTour={addPropertyToTour}
              updateState={updateState}
              submitAppointment={submitAppointment}
              clearSelected={clearSelected}
              moveToNextStep={moveToNextStep}
              moveToPreviousStep={moveToPreviousStep}
            />
          )}
        </MainContainer>
      )}
    </>
  );

  return (
    <InaccessibleListingPanelWrapper
      dataLoaded={state.dataLoaded}
      listingFound={Boolean(state.listing)}
      canBookAppointment={state.canBookAppointment}
      listingVisibility={state.listing?.visibility}
      listingOrgFeatures={state.listing?.org?.features}
    >
      {buildPage()}
    </InaccessibleListingPanelWrapper>
  );
};
export default AppointmentBookingInternal;
